import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogContent,
  CircularProgress
} from '@mui/material';
import { fetchApi } from './fetchApi';

interface ParentArtikel {
  id: number;
  sku: string;
  bezeichnung: string;
}

interface ChildFormularDaten {
  parent_id: string;
  sku: string;
  bezeichnung: string;
  farbe: string;
  groesse: string;

  ean: string;
  asin: string;
  fnsku: string;
  amazon_sku: string;
  shopify_variant_id: string;

  mindestbestand: string;
  bild_url: string;
}

type PresignResponse = {
  key: string;
  uploadUrl: string;
  publicUrl: string;
  error?: string;
};

export default function ChildFormular() {
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);

  const [parents, setParents] = useState<ParentArtikel[]>([]);

  // Nach dem POST merken wir uns die neue ID
  const [createdArtikelId, setCreatedArtikelId] = useState<number | null>(null);

  // Upload UI
  const [uploadLaden, setUploadLaden] = useState(false);
  const [uploadFehler, setUploadFehler] = useState<string | null>(null);
  const [bildDialogOpen, setBildDialogOpen] = useState(false);

  const [child, setChild] = useState<ChildFormularDaten>({
    parent_id: '',
    sku: '',
    bezeichnung: '',
    farbe: '',
    groesse: '',
    ean: '',
    asin: '',
    fnsku: '',
    amazon_sku: '',
    shopify_variant_id: '',
    mindestbestand: '',
    bild_url: ''
  });

  useEffect(() => {
    async function ladeElternArtikel() {
      try {
        const res = await fetchApi('/api/artikel?typ=parent');
        const data = await res.json();
        setParents(data);
      } catch (err) {
        console.error('Fehler beim Laden der Parent-Artikel:', err);
      }
    }
    ladeElternArtikel();
  }, []);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setChild(prev => ({ ...prev, [name]: value }));
  }

  function handleParentChange(e: SelectChangeEvent) {
    setChild(prev => ({ ...prev, parent_id: e.target.value }));
  }

  async function saveBildUrlToDb(artikelId: number, publicUrl: string | null) {
    const resp = await fetchApi(`/api/artikel/${artikelId}/bild`, {
  method: 'PATCH',
  body: JSON.stringify({ bild_url: publicUrl })
});


    const data = await resp.json().catch(() => null);

    if (!resp.ok) {
      const msg = data?.error || `Bild-URL speichern fehlgeschlagen (HTTP ${resp.status}).`;
      throw new Error(msg);
    }
  }

  // -------------------------------------------------------------
  // BILD UPLOAD (nur möglich, wenn createdArtikelId existiert)
  // -------------------------------------------------------------
  async function handleBildDatei(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] || null;
    e.target.value = '';
    if (!file) return;

    setUploadFehler(null);
    setErfolg(null);

    if (!createdArtikelId) {
      setUploadFehler('Bitte zuerst den Artikel speichern – danach kann das Bild hochgeladen werden.');
      return;
    }

    const okTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!okTypes.includes(file.type)) {
      setUploadFehler('Nur JPG/PNG/WebP erlaubt.');
      return;
    }

    const maxBytes = 3 * 1024 * 1024;
    if (file.size > maxBytes) {
      setUploadFehler('Max. 3 MB erlaubt.');
      return;
    }

    setUploadLaden(true);

    try {
      // 1) Presign holen
      const presignResp = await fetchApi('/api/uploads/artikelbild/presign', {
  method: 'POST',
  body: JSON.stringify({
    artikel_id: createdArtikelId,
    content_type: file.type,
    file_size: file.size
  })
});

      const presignData = (await presignResp.json().catch(() => null)) as PresignResponse | null;

      if (!presignResp.ok) {
        throw new Error(presignData?.error || `Presign fehlgeschlagen (HTTP ${presignResp.status}).`);
      }
      if (!presignData?.uploadUrl || !presignData?.publicUrl) {
        throw new Error('Presign-Antwort unvollständig (uploadUrl/publicUrl fehlen).');
      }

      const uploadUrl = presignData.uploadUrl;
      const publicUrl = presignData.publicUrl;

      // 2) PUT Upload
      const putResp = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });

      if (!putResp.ok) {
        const txt = await putResp.text().catch(() => '');
        console.error('S3 PUT Fehler:', putResp.status, txt);
        throw new Error(`Upload fehlgeschlagen (HTTP ${putResp.status}).`);
      }

      // 3) DB speichern
      await saveBildUrlToDb(createdArtikelId, publicUrl);

      // 4) UI aktualisieren
      setChild(prev => ({ ...prev, bild_url: publicUrl }));
      setErfolg('Bild gespeichert.');
    } catch (err: any) {
      console.error(err);
      setUploadFehler(err?.message || 'Bild-Upload fehlgeschlagen.');
    } finally {
      setUploadLaden(false);
    }
  }

  // -------------------------------------------------------------
  // SPEICHERN (POST)
  // -------------------------------------------------------------
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    setErfolg(null);
    setUploadFehler(null);

    if (!child.parent_id || !child.sku) {
      setFehler('Parent und SKU sind Pflichtfelder.');
      return;
    }

    const payload = {
      sku: child.sku,
      parent_id: parseInt(child.parent_id, 10),

      // Child-spezifische Felder
      bezeichnung: child.bezeichnung || null,
      farbe: child.farbe || null,
      groesse: child.groesse || null,
      mindestbestand: child.mindestbestand ? parseInt(child.mindestbestand, 10) : null,

      ean: child.ean || null,
      asin: child.asin || null,
      fnsku: child.fnsku || null,
      amazon_sku: child.amazon_sku || null,
      shopify_variant_id: child.shopify_variant_id || null,

      // Bild wird nach Anlage separat gepatcht → hier erstmal null (oder vorhandene URL wenn du manuell willst)
      bild_url: child.bild_url || null,

      // Nicht Teil des Childs → beim Backend NULL:
      beschreibung: null,
      einkaufspreis: null,
      verkaufspreis: null,

      kategorie_id: null,
      lieferant_id: null,
      amazon_product_id: null,
      shopify_product_id: null,
      barcode_intern: null
    };

    try {
      const res = await fetchApi('/api/artikel', {
  method: 'POST',
  body: JSON.stringify(payload),
});


      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || 'Fehler beim Speichern');
      }

      const newId = Number(data?.artikel_id);
      if (!newId) {
        throw new Error('Artikel wurde angelegt, aber artikel_id fehlt in der Antwort.');
      }

      setCreatedArtikelId(newId);
      setErfolg(`Child-Artikel gespeichert (ID: ${newId}). Jetzt kannst du das Bild hochladen.`);
    } catch (err: any) {
      setFehler(
  err?.message === '401'
    ? 'Session abgelaufen. Bitte neu einloggen.'
    : (err.message || 'Unbekannter Fehler')
);

    }
  }

  return (
    <Container maxWidth="md">
      <Box mt={4} mb={2}>
        <Typography variant="h5">Child-Variante anlegen</Typography>
      </Box>

      {fehler && <Alert severity="error" sx={{ mb: 2 }}>{fehler}</Alert>}
      {uploadFehler && <Alert severity="error" sx={{ mb: 2 }}>{uploadFehler}</Alert>}
      {erfolg && <Alert severity="success" sx={{ mb: 2 }}>{erfolg}</Alert>}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>

          {/* Parent Auswahl */}
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel id="parent-id-label">Parent auswählen</InputLabel>
              <Select
                labelId="parent-id-label"
                value={child.parent_id}
                label="Parent auswählen"
                onChange={handleParentChange}
              >
                {parents.map((p) => (
                  <MenuItem key={p.id} value={p.id.toString()}>
                    {p.sku} – {p.bezeichnung}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Child Basisdaten */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Child-SKU"
              name="sku"
              value={child.sku}
              onChange={handleChange}
              fullWidth
              required
              disabled={createdArtikelId !== null} // nach Anlage nicht mehr ändern (weil SKU im Key steckt)
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Bezeichnung"
              name="bezeichnung"
              value={child.bezeichnung}
              onChange={handleChange}
              fullWidth
              disabled={createdArtikelId !== null}
            />
          </Grid>

          {/* Farbe & Größe */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Farbe"
              name="farbe"
              value={child.farbe}
              onChange={handleChange}
              fullWidth
              disabled={createdArtikelId !== null}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Größe"
              name="groesse"
              value={child.groesse}
              onChange={handleChange}
              fullWidth
              disabled={createdArtikelId !== null}
            />
          </Grid>

          {/* Mindestbestand */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Mindestbestand"
              name="mindestbestand"
              value={child.mindestbestand}
              onChange={handleChange}
              type="number"
              fullWidth
              disabled={createdArtikelId !== null}
            />
          </Grid>

          {/* Bild Upload Block */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 2, p: 2, height: '100%' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Artikelbild (nach dem Speichern)
              </Typography>

              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Button variant="outlined" component="label" disabled={uploadLaden || !createdArtikelId}>
                  Bild auswählen & hochladen
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    hidden
                    onChange={handleBildDatei}
                  />
                </Button>

                {uploadLaden && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Upload läuft…</Typography>
                  </Box>
                )}

                {child.bild_url ? (
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: 1,
                      border: '1px solid rgba(0,0,0,0.12)',
                      overflow: 'hidden',
                      cursor: 'pointer'
                    }}
                    title="Klicken zum Vergrößern"
                    onClick={() => setBildDialogOpen(true)}
                  >
                    <Box
                      component="img"
                      src={child.bild_url}
                      alt="Artikelbild"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    {createdArtikelId ? 'Noch kein Bild.' : 'Erst speichern, dann hochladen.'}
                  </Typography>
                )}
              </Box>

              <Box mt={2}>
                <TextField
                  label="Bild URL (automatisch)"
                  fullWidth
                  value={child.bild_url}
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Box>
          </Grid>

          {/* EAN / Amazon / Shopify */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="EAN"
              name="ean"
              value={child.ean}
              onChange={handleChange}
              fullWidth
              disabled={createdArtikelId !== null}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="ASIN"
              name="asin"
              value={child.asin}
              onChange={handleChange}
              fullWidth
              disabled={createdArtikelId !== null}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="FNSKU"
              name="fnsku"
              value={child.fnsku}
              onChange={handleChange}
              fullWidth
              disabled={createdArtikelId !== null}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Amazon SKU"
              name="amazon_sku"
              value={child.amazon_sku}
              onChange={handleChange}
              fullWidth
              disabled={createdArtikelId !== null}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Shopify Variant ID"
              name="shopify_variant_id"
              value={child.shopify_variant_id}
              onChange={handleChange}
              fullWidth
              disabled={createdArtikelId !== null}
            />
          </Grid>

          {/* Speichern */}
          <Grid item xs={12} display="flex" gap={2}>
            <Button variant="contained" color="primary" type="submit" disabled={createdArtikelId !== null}>
              Speichern
            </Button>

            {createdArtikelId !== null && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => window.location.reload()}
              >
                Neues Child anlegen
              </Button>
            )}
          </Grid>

        </Grid>
      </form>

      {/* Bild-Dialog groß */}
      <Dialog open={bildDialogOpen} onClose={() => setBildDialogOpen(false)} maxWidth="lg">
        <DialogContent sx={{ p: 1 }}>
          {child.bild_url && (
            <Box
              component="img"
              src={child.bild_url}
              alt="Artikelbild groß"
              sx={{ width: '100%', height: 'auto', display: 'block', maxWidth: 1100 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
